/**
 * MonthView Component
 *
 * Displays bookings in a month view format with employee rows
 * and a grid of days grouped by week.
 *
 * Uses the Accordion component for expandable employee sections.
 */

import { Accordion } from "@/components/ui/display/Accordion";
import { Legends } from "@/components/booking/display/Legends";
import React, { useMemo, useCallback } from "react";
import styles from "../Schedule.module.css";
import type { MonthViewProps, WeekGroup } from "../types";
import { MonthEmployeeSection } from "./MonthEmployeeSection";

export const MonthView: React.FC<MonthViewProps> = React.memo(
  ({
    employees,
    hideWeekend,
    hoursInDay,
    collapsedEmployees,
    toggleEmployeeCollapse,
    selectMode,
    selectedCardId,
    selectedIds, // Used to trigger React.memo re-render when selection changes
    isSelected,
    isInRangePreview,
    setRangeHoverTarget,
    addToSelection,
    removeFromSelection,
    handleCardClick,
    onCardClick,
    onViewModeChange,
    getEmployeeStats,
    getBookingsForDay,
    getCardForDay,
    weekGroups,
    daysPerWeek,
    getWeeklySum,
    hideAggregate = false,
    showIndividualEditButtons = false,
    editingEmployeeId,
    onEnterEmployeeEditMode,
    onExitEmployeeEditMode,
  }) => {
    /**
     * Handle week label click
     * In edit mode: toggle selection of all days in the week for all employees
     * In view mode: navigate to week view
     */
    const handleWeekLabelClick = useCallback(
      (week: WeekGroup) => {
        if (selectMode) {
          // In edit mode: toggle selection for this week across all employees
          const cardIds: string[] = [];
          for (const employee of employees) {
            for (const day of week.days) {
              if (hideWeekend && day.isWeekend) continue;
              const cardData = getCardForDay(employee, day.date);
              cardIds.push(cardData.id);
            }
          }

          const allSelected = cardIds.every((id) => isSelected(id));

          if (allSelected) {
            removeFromSelection(cardIds);
          } else {
            addToSelection(cardIds);
          }
        } else {
          onViewModeChange("week", week.startDate);
        }
      },
      [
        selectMode,
        employees,
        hideWeekend,
        getCardForDay,
        isSelected,
        removeFromSelection,
        addToSelection,
        onViewModeChange,
      ],
    );

    // Convert collapsed set to expanded array for Accordion (inverted logic)
    const expandedValues = useMemo(() => {
      return employees
        .filter((emp) => !collapsedEmployees.has(emp.id))
        .map((emp) => String(emp.id));
    }, [employees, collapsedEmployees]);

    // Handle accordion value change - toggle the employee
    const handleAccordionChange = useCallback(
      (value: string | string[]) => {
        const newExpanded = Array.isArray(value) ? value : [value];

        for (const employee of employees) {
          const empId = String(employee.id);
          const wasExpanded = expandedValues.includes(empId);
          const isNowExpanded = newExpanded.includes(empId);

          if (wasExpanded !== isNowExpanded) {
            toggleEmployeeCollapse(employee.id);
          }
        }
      },
      [employees, expandedValues, toggleEmployeeCollapse],
    );

    return (
      <div className={styles.monthView} data-testid="schedule-month-view">
        <Accordion
          type="multiple"
          value={expandedValues}
          onValueChange={handleAccordionChange}
        >
          {employees.map((employee) => (
            <MonthEmployeeSection
              key={employee.id}
              employee={employee}
              stats={getEmployeeStats(employee)}
              weekGroups={weekGroups}
              daysPerWeek={daysPerWeek}
              hideWeekend={hideWeekend}
              hoursInDay={hoursInDay}
              hideAggregate={hideAggregate}
              selectMode={selectMode}
              selectedCardId={selectedCardId}
              selectionCount={selectedIds.size}
              isSelected={isSelected}
              isInRangePreview={isInRangePreview}
              setRangeHoverTarget={setRangeHoverTarget}
              editingEmployeeId={editingEmployeeId ?? null}
              showIndividualEditButtons={showIndividualEditButtons}
              getBookingsForDay={getBookingsForDay}
              getCardForDay={getCardForDay}
              getWeeklySum={getWeeklySum}
              handleCardClick={handleCardClick}
              onWeekLabelClick={handleWeekLabelClick}
              onCardClick={onCardClick}
              onEnterEmployeeEditMode={onEnterEmployeeEditMode}
              onExitEmployeeEditMode={onExitEmployeeEditMode}
            />
          ))}
        </Accordion>

        <Legends direction="row" align="center" />
      </div>
    );
  },
);

MonthView.displayName = "MonthView";

export default MonthView;
